import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { apiFetch } from '../api';
import Modal from '../components/Modal';
import InfoTip from '../components/InfoTip';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, EmptyState, Field, Icon, PageHeader, Switch, TableSkeleton, Tabs,
} from '../components/ui';
import usePhoneCalls from '../utils/usePhoneCalls';

const EMPTY_FORM = {
  name: '',
  monthly_cost_limit: '',
  backup_monthly_cost_limit: '',
  llm_provider: '', llm_model: '', llm_custom_key: '',
  backup_llm_provider: '', backup_llm_model: '', backup_custom_key: '',
  free_model_provider: '', free_model: '', free_model_key: '',
  subagent_llm_model: '',
  failover_llm_model: '',
  // Phone calls (the Phone calls tab): voice_models keys, the voice, and an llm_models key.
  call_s2t_model: '', call_t2s_model: '', call_t2s_voice: '', call_t2t_model: '',
  is_default: false,
};

const CALL_FIELDS = ['call_s2t_model', 'call_t2s_model', 'call_t2s_voice', 'call_t2t_model'];

const EDITOR_TABS = [
  { value: 'models', label: 'Models', icon: 'models' },
  { value: 'phone', label: 'Phone calls', icon: 'phone' },
];

// The T2S providers that can speak on a call: they stream raw audio piece by piece
// (proxy/shared/speech.py PHONE_T2S_PROVIDERS). Edge TTS gives MP3 only.
const PHONE_T2S_PROVIDERS = ['elevenlabs', 'openai-speech', 'kokoro'];

const PHONE_HELP = 'A call to a user on this tier listens with the speech-to-text model, '
  + 'speaks with the voice, and holds its side of the conversation with the call model: it '
  + 'decides when the caller has finished, answers small talk itself, hands everything else '
  + 'to the user\'s agent, and turns the agent\'s reply into speech. Every one of those calls '
  + 'is billed to the user like their agent\'s, under the source Phone, and only from the main '
  + 'and backup budgets: once both are spent, the call ends. Until all three are set, calls to '
  + 'the tier\'s users are refused.';

const tierCallsOn = (t) => CALL_FIELDS.every(f => !!t[f]);

// slot prefix → custom-key form field
const KEY_FIELD = {
  llm: 'llm_custom_key',
  backup_llm: 'backup_custom_key',
  free_model: 'free_model_key',
};

// slot prefix → model form field. The model column is regular for main/backup
// (`{slot}_model`) but IRREGULAR for free: its column is `free_model`, NOT
// `free_model_model`. The generic `${slot}_model` pattern would write/read a
// phantom `free_model_model` key, so picking a free model never reached the
// `free_model` field buildPayload sends → the change silently didn't stick.
// (Provider is regular for all three: `{slot}_provider`.)
const MODEL_FIELD = {
  llm: 'llm_model',
  backup_llm: 'backup_llm_model',
  free_model: 'free_model',
  // Not a link of the chain: the model sub-agents and workflow subtasks run on.
  subagent: 'subagent_llm_model',
  // Not a link either: the model a call goes to when its own model's endpoint refuses the
  // connection.
  failover: 'failover_llm_model',
  // The model a phone call's own conversation runs on (the Phone calls tab).
  call_t2t: 'call_t2t_model',
};

const SUBAGENT_HELP = 'The model the agent\'s sub-agents and workflow subtasks run on, for '
  + 'example the main model at a lower reasoning level, so background work is faster and '
  + 'cheaper while the conversation keeps the main model. "Same as main" uses the main model. '
  + 'It shares the main monthly limit (billed at this model\'s own prices) and the fallback '
  + 'chain: once the main limit is reached, sub-agents move to Backup and Free with everything '
  + 'else. The main custom API key is not sent to it.';

const FAILOVER_HELP = 'The model a request goes to when the endpoint of the model it was '
  + 'routed to refuses the connection: one of the models above, or a platform model such as '
  + 'vision or proofreading. For example Bedrock, for when the AI Factory is down. Only a '
  + 'refused connection counts, the sign of an endpoint that is not up at all. Everything else '
  + 'is returned as it is: an error answer (a 502, 503 or 504 too), a rejected API key, a rate '
  + 'limit, a reply that stops partway, a name that does not resolve, a failed TLS handshake or '
  + 'a connection that times out. It is billed at its own prices to the limit the request was '
  + 'already on, with the platform shared key, and it must be a model, not a router.';

// Under the failover select: what triggers it, in short. FAILOVER_HELP has the rest.
const FAILOVER_HINT = 'Used when a model\'s server is not running, so its endpoint refuses the '
  + 'connection: any model above, and the platform\'s own calls (vision, proofreading). An '
  + 'error answer, a timeout or a DNS or TLS failure is returned as it is.';

// The chain in the order the proxy walks it. `limitField` is the monthly cap that ends a
// slot; Free has none. `then` captions the arrow into the NEXT slot.
const SLOTS = [
  {
    slot: 'llm',
    step: 1,
    title: 'Main session',
    limitField: 'monthly_cost_limit',
    limitLabel: 'Main monthly limit (EUR)',
    limitHint: 'Leave empty for unlimited.',
    help: 'Primary model, used while the tier\'s main spend is under the main monthly limit. Cron tasks share this model and its quota, and so do sub-agents and workflow subtasks unless the Sub-agents slot below names their own model.',
    then: 'When the main limit is reached',
  },
  {
    slot: 'backup_llm',
    step: 2,
    title: 'Backup model',
    limitField: 'backup_monthly_cost_limit',
    limitLabel: 'Backup monthly limit (EUR)',
    limitHint: 'Empty or 0 means no backup.',
    emptyLabel: 'Same as main',
    help: 'Served once the main spend cap is reached, until the backup monthly limit is reached too. "Same as main" leaves the slot unset, so the chain falls through to Free, then refuses.',
    then: 'When both paid limits are reached',
  },
  {
    slot: 'free_model',
    step: 3,
    title: 'Free model',
    help: 'Fallback once both paid caps are exhausted. Unlimited for tier users: the token cost is absorbed by the platform and never charged against a quota.',
  },
];

// Why a tier cannot be deleted, or null when it can. Mirrors the two 409s the DELETE route
// returns, so the button says why it is off instead of failing on click.
const deleteBlocker = (tier) => {
  if (tier.is_default) return 'The default tier cannot be deleted, set another tier as default first';
  if (tier.user_count > 0) return 'Reassign assigned users before deleting';
  return null;
};

function Tiers() {
  const [tiers, setTiers] = useState([]);
  const [llmModels, setLlmModels] = useState([]);
  const [routers, setRouters] = useState([]);
  // The Voice page's catalog: speech models, each T2S provider's voices, and an ElevenLabs
  // row's account voices. What the Phone calls tab picks from.
  const [voiceCatalog, setVoiceCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null);          // null | "create" | "edit"
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tab, setTab] = useState('models');
  const [saving, setSaving] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  // Phone calls only where the environment takes them (voice_channel_enabled): elsewhere the
  // editor has no Phone calls tab and a save leaves the stored call models as they are.
  const phoneOn = usePhoneCalls();
  const shownTab = phoneOn ? tab : 'models';

  const fetchTiers = async () => {
    try {
      const resp = await apiFetch('/api/tiers');
      if (resp.ok) setTiers(await resp.json());
    } catch (e) {
      toast.error('Failed to load tiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiers();
    (async () => {
      try {
        const r = await apiFetch('/api/llm-models');
        if (r.ok) setLlmModels(await r.json());
      } catch (_) { /* ignore */ }
    })();
    (async () => {
      try {
        const r = await apiFetch('/api/llm-routers');
        if (r.ok) setRouters(await r.json());
      } catch (_) { /* ignore */ }
    })();
    (async () => {
      try {
        const r = await apiFetch('/api/voice-models');
        if (r.ok) setVoiceCatalog(await r.json());
      } catch (_) { /* the Phone calls tab says so */ }
    })();
  }, []);

  // Flat catalog: every model picker shows ui_name, the provider_key is resolved
  // from the picked row when we build the payload (the tier still stores it for
  // proxy-side tier dispatch, but admins don't see it).
  const activeModels = llmModels.filter(m => m.active);
  const isFreeModel = (m) => (m.price_input_per_m || 0) === 0 && (m.price_output_per_m || 0) === 0;
  // A slot may name a router (Routers page) instead of a model. Its value is the
  // router's `slot`, "router:<key>", which no model key can take.
  const activeRouters = routers.filter(r => r.active);
  const routerFor = (value) => routers.find(r => r.slot === value);

  const setSlot = (slot, modelKey) => {
    const m = llmModels.find(x => x.model_key === modelKey);
    setForm(prev => ({
      ...prev,
      [`${slot}_provider`]: m?.provider_key || '',
      [MODEL_FIELD[slot]]: modelKey || '',
    }));
  };

  const startCreate = () => {
    setMode('create');
    setEditingId(null);
    setForm(EMPTY_FORM);
    setTab('models');
  };

  const startEdit = (tier) => {
    setMode('edit');
    setEditingId(tier.id);
    setTab('models');
    setForm({
      name: tier.name,
      monthly_cost_limit: tier.monthly_cost_limit !== null ? tier.monthly_cost_limit.toString() : '',
      backup_monthly_cost_limit: tier.backup_monthly_cost_limit != null ? tier.backup_monthly_cost_limit.toString() : '',
      // Model + per-slot custom-key. Keys are not echoed back from the API
      // (only `has_*_custom_key` flags), so the inputs start empty — submitting
      // empty preserves the existing key on edit.
      llm_provider: tier.llm_provider || '', llm_model: tier.llm_model || '', llm_custom_key: '',
      backup_llm_provider: tier.backup_llm_provider || '', backup_llm_model: tier.backup_llm_model || '', backup_custom_key: '',
      free_model_provider: tier.free_model_provider || '', free_model: tier.free_model || '', free_model_key: '',
      subagent_llm_model: tier.subagent_llm_model || '',
      failover_llm_model: tier.failover_llm_model || '',
      call_s2t_model: tier.call_s2t_model || '',
      call_t2s_model: tier.call_t2s_model || '',
      call_t2s_voice: tier.call_t2s_voice || '',
      call_t2t_model: tier.call_t2t_model || '',
      _has_llm_custom_key: !!tier.has_llm_custom_key,
      _has_backup_custom_key: !!tier.has_backup_custom_key,
      _has_free_model_key: !!tier.has_free_model_key,
      is_default: !!tier.is_default,
      // Original default state — used to lock the switch so the current default
      // can't be turned off (the default moves by turning it on for another tier).
      _was_default: !!tier.is_default,
    });
  };

  const cancel = () => { setMode(null); setEditingId(null); setForm(EMPTY_FORM); };

  const buildPayload = (isCreate) => {
    const num = (v, unlimitedSentinel) =>
      v === '' ? (isCreate ? null : unlimitedSentinel) : parseFloat(v);
    const payload = {
      name: form.name.trim(),
      monthly_cost_limit: num(form.monthly_cost_limit, -1),
      backup_monthly_cost_limit: num(form.backup_monthly_cost_limit, -1),
      llm_provider: form.llm_provider || (isCreate ? null : ''),
      llm_model: form.llm_model || (isCreate ? null : ''),
      backup_llm_provider: form.backup_llm_provider || (isCreate ? null : ''),
      backup_llm_model: form.backup_llm_model || (isCreate ? null : ''),
      free_model_provider: form.free_model_provider || (isCreate ? null : ''),
      free_model: form.free_model || (isCreate ? null : ''),
      // '' on edit puts the slot back to "Same as main".
      subagent_llm_model: form.subagent_llm_model || (isCreate ? null : ''),
      // '' on edit removes the failover.
      failover_llm_model: form.failover_llm_model || (isCreate ? null : ''),
    };
    // Phone calls: '' on edit unsets a field. Not sent where there is no Phone calls tab.
    if (phoneOn) for (const f of CALL_FIELDS) payload[f] = form[f] || (isCreate ? null : '');
    // Custom keys: on create, send NULL when blank. On edit, only send when the
    // user typed a value (omit otherwise so we don't accidentally clear the
    // existing key — clearing is done via the explicit Clear button).
    for (const f of ['llm_custom_key', 'backup_custom_key', 'free_model_key']) {
      if (isCreate) {
        payload[f] = form[f] || null;
      } else if (form[f]) {
        payload[f] = form[f];
      }
    }
    // Explicit clear flags: when set, send empty string which the backend treats as NULL.
    for (const [flag, f] of [['_clear_llm_custom_key', 'llm_custom_key'],
                              ['_clear_backup_custom_key', 'backup_custom_key'],
                              ['_clear_free_model_key', 'free_model_key']]) {
      if (form[flag]) payload[f] = '';
    }
    // Default flag. Only send `true` (promote this tier); never send `false` for
    // the current default — the backend rejects un-defaulting, and the switch is
    // locked on the current default anyway.
    if (form.is_default) payload.is_default = true;
    return payload;
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const isCreate = mode === 'create';
    const url = isCreate ? '/api/tiers' : `/api/tiers/${editingId}`;
    const method = isCreate ? 'POST' : 'PUT';
    setSaving(true);
    try {
      const resp = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload(isCreate)),
      });
      if (resp.ok) {
        toast.success(isCreate ? 'Tier created' : 'Tier updated');
        cancel();
        fetchTiers();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save tier');
      }
    } catch (e) { toast.error('Failed to save tier'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!await confirm({
      title: `Delete tier ${name}?`,
      body: <p>Users on this tier keep their accounts and are not reassigned.</p>,
      confirmLabel: 'Delete tier',
    })) return;
    try {
      const resp = await apiFetch(`/api/tiers/${id}`, { method: 'DELETE' });
      if (resp.ok) { toast.success('Tier deleted'); fetchTiers(); }
      else { const err = await resp.json().catch(() => ({})); toast.error(err.detail || 'Failed to delete'); }
    } catch (e) { toast.error('Failed to delete tier'); }
  };

  // The slot's saved value when the select does not list it: a model or router made
  // inactive after the tier named it. With no option of its own React shows the first one
  // ("No failover", "Same as main") while the form keeps the old key, so picking that one
  // fires no change, and every save is refused because the API checks every slot. A key
  // the catalog does not hold (still loading, or deleted) is shown as saved, with no verdict.
  const inactiveOption = (value, listed) => {
    if (!value || listed.includes(value)) return null;
    const known = routerFor(value) || llmModels.find(m => m.model_key === value);
    return <option value={value}>{known ? `${known.ui_name} (inactive)` : value}</option>;
  };

  const renderModelSelect = (slot, { freeOnly = false, modelsOnly = false, emptyLabel = '—', id } = {}) => {
    const value = form[MODEL_FIELD[slot]] || '';
    const options = activeModels.filter(m => !freeOnly || isFreeModel(m));
    const listed = options.map(m => m.model_key)
      .concat(modelsOnly ? [] : activeRouters.map(r => r.slot));
    const modelOptions = options.map(m => (
      <option key={m.model_key} value={m.model_key}>{m.ui_name}</option>
    ));
    return (
      <select id={id} className="form-input" value={value}
        onChange={e => setSlot(slot, e.target.value)}>
        <option value="">{emptyLabel}</option>
        {inactiveOption(value, listed)}
        {activeRouters.length && !modelsOnly ? (
          <>
            <optgroup label="Models">{modelOptions}</optgroup>
            <optgroup label="Routers">
              {activeRouters.map(r => <option key={r.slot} value={r.slot}>{r.ui_name}</option>)}
            </optgroup>
          </>
        ) : modelOptions}
      </select>
    );
  };

  // One link of the fallback chain: the model, the monthly cap that ends it (Main and
  // Backup), and the optional per-slot key.
  const renderSlot = ({ slot, step, title, help, limitField, limitLabel, limitHint,
                        freeOnly = false, emptyLabel = 'None' }) => {
    const keyField = KEY_FIELD[slot];
    const hasExisting = !!form[`_has_${keyField}`];
    const clearFlag = `_clear_${keyField}`;
    const isCleared = !!form[clearFlag];
    // Slot is "active" only if the chosen model_key resolves to a registered
    // active model. Unset slots ("Same as main" / "None") render as inactive
    // so we don't show a useless Custom API key input for a slot that won't
    // be used.
    const slotModel = form[MODEL_FIELD[slot]];
    const slotRouter = routerFor(slotModel);
    const slotActive = !!(slotModel && (llmModels.some(m => m.model_key === slotModel && m.active)
      || slotRouter?.active));
    const ids = { model: `tier-${slot}-model`, limit: `tier-${slot}-limit`, key: `tier-${slot}-key` };
    return (
      <div className="card-inset">
        <div className="row between mb-3">
          <h5 className="row gap-2 text-base fw-600">
            <Badge tone="accent" square>{step}</Badge>
            {title}
            {/* `help` was a <small> paragraph of 160-180 characters under each of the
                three slot labels, so ~500 characters of prose sat between the heading
                and the only control that matters. Same text, now behind the `i`. */}
            <InfoTip label={`About the ${title} slot`} width={340}>{help}</InfoTip>
          </h5>
          {!slotModel && <Badge tone="neutral">Not set</Badge>}
        </div>

        <div className="grid-2">
          <Field label="Model or router" htmlFor={ids.model} className="mb-0"
                 hint={slotRouter ? `The router picks among ${(slotRouter.candidates || []).length} models; its stated cost is charged.` : undefined}>
            {renderModelSelect(slot, { freeOnly, emptyLabel, id: ids.model })}
          </Field>
          {limitField ? (
            <Field label={limitLabel} htmlFor={ids.limit} hint={limitHint} className="mb-0">
              <div className="input-affix">
                <span className="affix-icon" aria-hidden="true">€</span>
                <input id={ids.limit} type="number" step="0.01" inputMode="decimal"
                  className="form-input num" value={form[limitField]}
                  onChange={e => setForm(p => ({ ...p, [limitField]: e.target.value }))} />
              </div>
            </Field>
          ) : (
            <div className="form-group mb-0">
              <div className="form-label">Monthly limit</div>
              <p className="text-sm text-muted">
                None. The platform absorbs the cost, so free usage never counts against a quota.
              </p>
            </div>
          )}
        </div>

        {slotActive && (
          <Field
            className="mt-4 mb-0"
            htmlFor={ids.key}
            label="Custom API key"
            extra={isCleared
              ? <Badge tone="danger">Will be cleared on save</Badge>
              : hasExisting && <Badge tone="ok" icon="check">Set</Badge>}
            hint={hasExisting && !isCleared
              ? 'A custom key is configured. Leave blank to keep it, or type a new one to replace it.'
              : `Optional. Leave blank to use the ${slotRouter ? 'router\'s own key' : 'platform shared key'}.`}
          >
            <div className="input-group">
              <input id={ids.key} type="text" className="form-input mono" autoComplete="off" spellCheck={false}
                value={form[keyField] || ''}
                onChange={e => setForm(p => ({ ...p, [keyField]: e.target.value, [clearFlag]: false }))} />
              {hasExisting && !isCleared && (
                <button type="button" className="btn btn-secondary"
                  onClick={() => setForm(p => ({ ...p, [clearFlag]: true, [keyField]: '' }))}>
                  Clear key
                </button>
              )}
              {isCleared && (
                <button type="button" className="btn btn-ghost"
                  onClick={() => setForm(p => ({ ...p, [clearFlag]: false }))}>
                  Keep key
                </button>
              )}
            </div>
          </Field>
        )}
      </div>
    );
  };

  // ── Phone calls ─────────────────────────────────────────────────────────────
  const speechModels = voiceCatalog?.models || [];
  const speechRow = (key) => speechModels.find(m => m.voice_key === key);
  const sttRows = speechModels.filter(m => m.streaming && m.active);
  const ttsRows = speechModels.filter(m => m.kind === 't2s' && m.active
                                       && PHONE_T2S_PROVIDERS.includes(m.provider));
  const phoneLanguage = voiceCatalog?.phone_language || 'it';

  // The voices a T2S row can speak a call with: an ElevenLabs row's are its account's,
  // Kokoro's those of the call's language, the speech API's its own (each speaks any).
  const phoneVoices = (row) => {
    if (!row) return { voices: [], error: null };
    if (row.provider === 'elevenlabs') {
      const listed = voiceCatalog?.account_voices?.[row.voice_key];
      return { voices: listed?.voices || [], error: listed?.error || null };
    }
    const all = voiceCatalog?.voices?.[row.provider] || [];
    return { voices: all.filter(v => !v.language || v.language === phoneLanguage), error: null };
  };

  const pickCallVoiceModel = (key) => {
    const { voices } = phoneVoices(speechRow(key));
    setForm(p => ({
      ...p,
      call_t2s_model: key,
      // The voice belongs to its model: kept when the new one has it, else its first.
      call_t2s_voice: voices.some(v => v.value === p.call_t2s_voice)
        ? p.call_t2s_voice : (voices[0]?.value || ''),
    }));
  };

  // A saved key the select does not list (switched off, or deleted), as inactiveOption does
  // for the model slots.
  const unlistedSpeechOption = (value, rows) => {
    if (!value || rows.some(r => r.voice_key === value)) return null;
    const known = speechRow(value);
    return <option value={value}>{known ? `${known.ui_name} (inactive)` : value}</option>;
  };

  const renderPhoneTab = () => {
    const on = tierCallsOn(form);
    const t2sRow = speechRow(form.call_t2s_model);
    const { voices, error: voicesError } = phoneVoices(t2sRow);
    const voiceListed = voices.some(v => v.value === form.call_t2s_voice);
    const typedVoice = t2sRow?.provider === 'elevenlabs' && voices.length === 0;
    let voiceHint = 'Pick the text-to-speech model first.';
    if (voicesError) {
      voiceHint = `The account's voices could not be listed (${voicesError}). Type a voice ID.`;
    } else if (t2sRow?.provider === 'kokoro') {
      voiceHint = 'Kokoro\'s Italian voices: a call is held in Italian.';
    } else if (t2sRow) {
      voiceHint = 'Any voice of the model: each speaks the call\'s language.';
    }
    return (
      <div className="stack gap-4" role="tabpanel" id="tier-panel-phone"
           aria-labelledby="tier-tab-phone">
        <div className="row between gap-3">
          <p className="text-sm text-muted m-0">
            What a phone call to a user on this tier listens, speaks and thinks with.{' '}
            <InfoTip label="About phone calls" width={360}>{PHONE_HELP}</InfoTip>
          </p>
          <Badge tone={on ? 'ok' : 'neutral'}>{on ? 'Calls on' : 'Calls off'}</Badge>
        </div>
        {!voiceCatalog && (
          <div className="notice notice-warn mb-0" role="status">
            <Icon name="alert" />
            <div className="notice-body">The speech models did not load, so only the saved
              keys are shown. Reload the page to pick others.</div>
          </div>
        )}

        <div className="card-inset">
          <Field label="Speech to text" htmlFor="tier-call-s2t" className="mb-0"
                 hint="A realtime speech-to-text model from the Voice page: the caller is heard as they speak.">
            <select id="tier-call-s2t" className="form-input" value={form.call_s2t_model}
                    onChange={e => setForm(p => ({ ...p, call_s2t_model: e.target.value }))}>
              <option value="">Not set</option>
              {unlistedSpeechOption(form.call_s2t_model, sttRows)}
              {sttRows.map(m => <option key={m.voice_key} value={m.voice_key}>{m.ui_name}</option>)}
            </select>
          </Field>
        </div>

        <div className="card-inset">
          <div className="grid-2">
            <Field label="Text to speech" htmlFor="tier-call-t2s" className="mb-0"
                   hint="ElevenLabs, a speech API or Kokoro. Edge TTS cannot stream to a phone line.">
              <select id="tier-call-t2s" className="form-input" value={form.call_t2s_model}
                      onChange={e => pickCallVoiceModel(e.target.value)}>
                <option value="">Not set</option>
                {unlistedSpeechOption(form.call_t2s_model, ttsRows)}
                {ttsRows.map(m => <option key={m.voice_key} value={m.voice_key}>{m.ui_name}</option>)}
              </select>
            </Field>
            <Field label="Voice" htmlFor="tier-call-voice" className="mb-0" hint={voiceHint}>
              {typedVoice ? (
                <input id="tier-call-voice" type="text" className="form-input mono"
                       value={form.call_t2s_voice} placeholder="Voice ID"
                       autoComplete="off" spellCheck={false}
                       onChange={e => setForm(p => ({ ...p, call_t2s_voice: e.target.value.trim() }))} />
              ) : (
                <select id="tier-call-voice" className="form-input" value={form.call_t2s_voice}
                        disabled={!t2sRow}
                        onChange={e => setForm(p => ({ ...p, call_t2s_voice: e.target.value }))}>
                  <option value="">{t2sRow ? 'Pick a voice' : 'Not set'}</option>
                  {form.call_t2s_voice && !voiceListed && (
                    <option value={form.call_t2s_voice}>{form.call_t2s_voice}</option>
                  )}
                  {voices.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              )}
            </Field>
          </div>
        </div>

        <div className="card-inset">
          <Field label="Call model" htmlFor="tier-call-t2t" className="mb-0"
                 hint="Runs the call's side of the conversation: when the caller has finished, small talk, and the agent's reply turned into speech. Use a fast model, a router cannot do this.">
            {renderModelSelect('call_t2t', { modelsOnly: true, emptyLabel: 'Not set', id: 'tier-call-t2t' })}
          </Field>
        </div>
      </div>
    );
  };

  // Resolve a model_key to its display ui_name. provider_key is no longer
  // part of the lookup — model_key is unique and self-identifies the row.
  const nameFor = (model_key) => {
    if (!model_key) return null;
    const r = routerFor(model_key);
    if (r) return `${r.ui_name} (router)`;
    const m = llmModels.find(x => x.model_key === model_key);
    return m ? m.ui_name : null;
  };

  const modelSummary = (tier) => {
    const mainName = nameFor(tier.llm_model);
    if (!mainName) {
      return (
        <span className="row gap-1 text-danger text-sm fw-500">
          <Icon name="alertCircle" size={14} /> No main model configured
        </span>
      );
    }
    // Backup falls back to Main when unset. Free has no fallback —
    // only shows when explicitly set. Sub-agent traffic shares Main.
    const backupName = nameFor(tier.backup_llm_model) || mainName;
    const freeName = nameFor(tier.free_model);
    const subName = nameFor(tier.subagent_llm_model);
    const failoverName = nameFor(tier.failover_llm_model);
    // Phone calls: shown once they are on, by the model the call's conversation runs on.
    const phoneName = phoneOn && tierCallsOn(tier)
      ? (nameFor(tier.call_t2t_model) || tier.call_t2t_model) : null;
    return (
      <dl className="text-sm m-0"
          style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '2px var(--sp-3)' }}>
        <dt className="text-muted">Main</dt><dd>{mainName}</dd>
        <dt className="text-muted">Backup</dt><dd>{backupName}</dd>
        {freeName && <><dt className="text-muted">Free</dt><dd>{freeName}</dd></>}
        {subName && <><dt className="text-muted">Sub-agents</dt><dd>{subName}</dd></>}
        {failoverName && <><dt className="text-muted">If refused</dt><dd>{failoverName}</dd></>}
        {phoneName && <><dt className="text-muted">Phone</dt><dd>{phoneName}</dd></>}
      </dl>
    );
  };

  const money = (v) => <span className="num">€{v.toFixed(2)}<span className="text-muted">/mo</span></span>;
  const editingName = (tiers.find(t => t.id === editingId) || {}).name || form.name;
  const creating = mode === 'create';

  return (
    <div>
      <PageHeader
        description={phoneOn
          ? 'What each subscription level allows: the models or routers it uses, its spend caps, and what its phone calls run on.'
          : 'What each subscription level allows: the models or routers it uses and its spend caps.'}
        actions={(
          <button type="button" className="btn btn-primary" onClick={startCreate}>
            <Icon name="plus" /> New tier
          </button>
        )}
      >
        <h1 className="page-title">
          Tiers
          {!loading && <Badge tone="neutral" size="lg">{tiers.length}</Badge>}
        </h1>
      </PageHeader>

      <div className="card card-flush">
        {loading ? (
          <TableSkeleton rows={4} cols={6} label="Loading tiers" />
        ) : tiers.length === 0 ? (
          <EmptyState
            icon="tiers"
            title="No tiers yet"
            action={<button type="button" className="btn btn-primary" onClick={startCreate}>Create the first tier</button>}
          >
            A tier picks the models a user&apos;s agent runs on and caps what it spends each month.
          </EmptyState>
        ) : (
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th className="num">Main limit</th>
                  <th className="num">Backup limit</th>
                  <th>Models</th>
                  <th className="num">Users</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {tiers.map(tier => {
                  const blocker = deleteBlocker(tier);
                  return (
                    <tr key={tier.id}>
                      <td data-label="Tier">
                        <div className="row-wrap gap-2">
                          <span className="cell-primary">{tier.name}</span>
                          {tier.is_default && <Badge tone="accent">Default</Badge>}
                        </div>
                      </td>
                      <td data-label="Main limit" className="num">
                        {tier.monthly_cost_limit !== null
                          ? money(tier.monthly_cost_limit)
                          : <span className="text-faint">Unlimited</span>}
                      </td>
                      <td data-label="Backup limit" className="num">
                        {tier.backup_monthly_cost_limit != null
                          ? money(tier.backup_monthly_cost_limit)
                          : <span className="text-faint">—</span>}
                      </td>
                      <td data-label="Models" className="wrap">{modelSummary(tier)}</td>
                      <td data-label="Users" className="num">{tier.user_count || 0}</td>
                      <td data-label="Actions">
                        <div className="row gap-1 row-end">
                          <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                  aria-label={`Edit ${tier.name}`} title="Edit tier"
                                  onClick={() => startEdit(tier)}>
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="btn btn-danger-soft btn-sm btn-icon"
                                  aria-label={blocker ? `Delete ${tier.name}: ${blocker}` : `Delete ${tier.name}`}
                                  title={blocker || 'Delete tier'}
                                  disabled={!!blocker}
                                  onClick={() => handleDelete(tier.id, tier.name)}>
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={mode !== null}
        onClose={() => { if (!saving) cancel(); }}
        title={creating ? 'New tier' : `Edit tier ${editingName}`}
        maxWidth={720}
      >
        <div className="stack gap-4">
          <Field
            label="Name"
            htmlFor="tier-name"
            required
            className="mb-0"
            hint={creating
              ? 'Lower-cased, this becomes the tier\'s SKU in the producer API.'
              : 'Lower-cased, this is also the tier\'s SKU in the producer API, so renaming changes what producers send.'}
          >
            <input id="tier-name" type="text" className="form-input" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Pro" />
          </Field>

          <Switch
            checked={!!form.is_default}
            disabled={form._was_default}
            onChange={(v) => setForm(p => ({ ...p, is_default: v }))}
            label="Set as default tier"
            description={form._was_default
              ? 'This is the current default tier. To move the default, edit another tier and turn this on there.'
              : 'New users created without an explicit tier (free-trial onboarding) land on the default tier.'}
          />

          {/* No strip where calls are off: one tab would be a heading in disguise. */}
          {phoneOn && (
            <Tabs tabs={EDITOR_TABS} value={shownTab} onChange={setTab} variant="line"
                  label="Tier settings" idPrefix="tier" />
          )}

          {shownTab === 'phone' && renderPhoneTab()}

          {shownTab === 'models' && (
            <div className="stack gap-4"
                 {...(phoneOn ? { role: 'tabpanel', id: 'tier-panel-models',
                                  'aria-labelledby': 'tier-tab-models' } : {})}>
              <div className="stack gap-2">
                <div>
                  <h4 className="text-md fw-600">Model composition</h4>
                  <p className="text-sm text-muted mt-1">
                    Requests go to the first model in the chain whose monthly limit is not yet reached.
                  </p>
                </div>
                {SLOTS.map((s, i) => (
                  <React.Fragment key={s.slot}>
                    {renderSlot(s)}
                    {i < SLOTS.length - 1 && (
                      <div className="row center gap-2 text-sm text-muted">
                        <Icon name="arrowDown" size={14} />
                        <span>{s.then}</span>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Outside the chain on purpose: it does not end where a limit is reached, it
                  decides which model the agent's background runs use while Main is in force. */}
              <div className="card-inset">
                <div className="row between mb-3">
                  <h5 className="row gap-2 text-base fw-600">
                    Sub-agents and workflow subtasks
                    <InfoTip label="About the sub-agent model" width={340}>{SUBAGENT_HELP}</InfoTip>
                  </h5>
                  {!form.subagent_llm_model && <Badge tone="neutral">Same as main</Badge>}
                </div>
                <Field label="Model or router" htmlFor="tier-subagent-model" className="mb-0"
                       hint={routerFor(form.subagent_llm_model)
                         ? 'The router picks among its models; its stated cost is charged to the main limit.'
                         : undefined}>
                  {renderModelSelect('subagent', { emptyLabel: 'Same as main', id: 'tier-subagent-model' })}
                </Field>
              </div>

              {/* Outside the chain too: it is not reached by spending, only by an endpoint that
                  refuses the connection, whichever slot above or platform model the request
                  was on. */}
              <div className="card-inset">
                <div className="row between mb-3">
                  <h5 className="row gap-2 text-base fw-600">
                    If a model&apos;s endpoint refuses the connection
                    <InfoTip label="About the failover model" width={340}>{FAILOVER_HELP}</InfoTip>
                  </h5>
                  {!form.failover_llm_model && <Badge tone="neutral">No failover</Badge>}
                </div>
                <Field label="Model" htmlFor="tier-failover-model" className="mb-0"
                       hint={FAILOVER_HINT}>
                  {renderModelSelect('failover', { modelsOnly: true, emptyLabel: 'No failover', id: 'tier-failover-model' })}
                </Field>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={cancel} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving
              ? <><span className="spinner-sm" /> Saving…</>
              : (creating ? 'Create tier' : 'Save tier')}
          </button>
        </div>
      </Modal>

      {confirmDialog}
    </div>
  );
}

export default Tiers;
